// SWOT (FODA) analyses of the market or a segment and their items.
import type { Identifier } from "ra-core";

import { isCompetitorLine } from "@/components/market-intel/labels";
import type { CompetitorLine } from "@/components/market-intel/types";

import {
  optionalText,
  optionalUrl,
  parseOptionalDay,
  type FormResult,
} from "./forms";
import type { MarketSwotAnalysis, MarketSwotItem, SwotQuadrant } from "./types";

/** Grid order: strengths, weaknesses (internal), opportunities, threats. */
export const SWOT_QUADRANTS: readonly SwotQuadrant[] = [
  "fortaleza",
  "debilidad",
  "oportunidad",
  "amenaza",
];

export const isSwotQuadrant = (value: unknown): value is SwotQuadrant =>
  SWOT_QUADRANTS.includes(value as SwotQuadrant);

export const swotQuadrantLabelKey = (quadrant: SwotQuadrant): string =>
  `competition.swot.quadrants.${isSwotQuadrant(quadrant) ? quadrant : "fortaleza"}`;

/** Latest analysis first. */
export const sortAnalyses = (
  analyses: readonly MarketSwotAnalysis[],
): MarketSwotAnalysis[] =>
  [...analyses].sort(
    (a, b) =>
      b.analysis_date.localeCompare(a.analysis_date) ||
      Number(b.id) - Number(a.id),
  );

/** The analysis to show: the one in the URL if it exists, else the latest. */
export const selectAnalysis = (
  analyses: readonly MarketSwotAnalysis[],
  wanted: string | null,
): MarketSwotAnalysis | null =>
  analyses.find((analysis) => String(analysis.id) === wanted) ??
  sortAnalyses(analyses)[0] ??
  null;

/** Items of each quadrant, by position then insertion. */
export const itemsByQuadrant = (
  items: readonly MarketSwotItem[],
): Record<SwotQuadrant, MarketSwotItem[]> => {
  const grouped: Record<SwotQuadrant, MarketSwotItem[]> = {
    fortaleza: [],
    debilidad: [],
    oportunidad: [],
    amenaza: [],
  };
  const sorted = [...items].sort(
    (a, b) =>
      (a.position ?? 0) - (b.position ?? 0) || Number(a.id) - Number(b.id),
  );
  for (const item of sorted)
    if (isSwotQuadrant(item.quadrant)) grouped[item.quadrant].push(item);
  return grouped;
};

/** Position for a new item: after the last one of its quadrant. */
export const nextPosition = (items: readonly MarketSwotItem[]): number =>
  items.reduce((max, item) => Math.max(max, item.position ?? 0), 0) + 1;

// ---------- forms ----------

export interface SwotAnalysisFormValues {
  title: string;
  segment: string;
  line: CompetitorLine | "";
  analysis_date: string;
  source: string;
  source_url: string;
  summary: string;
}

export type SwotAnalysisPayload = Pick<
  MarketSwotAnalysis,
  | "title"
  | "segment"
  | "line"
  | "analysis_date"
  | "source"
  | "source_url"
  | "summary"
>;

export const swotAnalysisFormDefaults = (
  today: string,
  analysis?: Partial<MarketSwotAnalysis>,
): SwotAnalysisFormValues => ({
  title: analysis?.title ?? "",
  segment: analysis?.segment ?? "",
  line: isCompetitorLine(analysis?.line) ? analysis.line : "",
  analysis_date: analysis?.analysis_date ?? today,
  source: analysis?.source ?? "",
  source_url: analysis?.source_url ?? "",
  summary: analysis?.summary ?? "",
});

export const normalizeSwotAnalysisForm = (
  values: SwotAnalysisFormValues,
): FormResult<SwotAnalysisPayload> => {
  const title = values.title.trim();
  if (!title) return { ok: false, error: "competition.form.title_required" };
  if (title.length > 200)
    return { ok: false, error: "competition.form.title_too_long" };
  const day = parseOptionalDay(values.analysis_date);
  if (!day.ok || !day.day)
    return { ok: false, error: "competition.form.date_invalid" };
  return {
    ok: true,
    data: {
      title,
      segment: optionalText(values.segment),
      line: isCompetitorLine(values.line) ? values.line : null,
      analysis_date: day.day,
      source: optionalText(values.source),
      source_url: optionalUrl(values.source_url),
      summary: optionalText(values.summary),
    },
  };
};

export interface SwotItemFormValues {
  quadrant: SwotQuadrant;
  content: string;
  /** "" = no competitor mentioned. */
  competitor_id: string;
}

export type SwotItemPayload = Pick<
  MarketSwotItem,
  "analysis_id" | "quadrant" | "content" | "competitor_id"
>;

export const normalizeSwotItemForm = (
  analysisId: Identifier,
  values: SwotItemFormValues,
  competitorIds: readonly Identifier[],
): FormResult<SwotItemPayload> => {
  const content = values.content.trim();
  if (!content)
    return { ok: false, error: "competition.swot.item_form.content_required" };
  if (content.length > 1000)
    return { ok: false, error: "competition.swot.item_form.content_too_long" };
  return {
    ok: true,
    data: {
      analysis_id: analysisId,
      quadrant: isSwotQuadrant(values.quadrant) ? values.quadrant : "fortaleza",
      content,
      competitor_id:
        competitorIds.find((id) => String(id) === values.competitor_id) ?? null,
    },
  };
};
